import { cn } from "../../utils";
import { typographyVariants } from "./typography-utils";

type HeadingVariant = "h1" | "h2" | "h3" | "h4";
export type HeadingWeight = "medium" | "semibold";
export type BodyWeight = "regular" | "medium";
export type BodySize = "lg" | "regular" | "sm" | "xs" | "xxs";
type ButtonSize = "xs" | "sm" | "md";
export type PlaceholderSize = "regular" | "large";

// Semantic variants with fixed styling (no weight/size props)
export type SemanticVariant = "page-heading" | "section-heading";

// All possible variant types
type TypographyVariant =
  | HeadingVariant
  | "body"
  | "button"
  | "table-header"
  | "placeholder"
  | "subheading"
  | "caption"
  | SemanticVariant;

// Conditional type to map variant to allowed weights
type WeightForVariant<V extends TypographyVariant> = V extends
  | HeadingVariant
  | "subheading"
  | "table-header"
  ? HeadingWeight
  : V extends "body" | "caption"
    ? BodyWeight
    : never;

// HTML attributes for different element types
type HeadingHTMLAttributes = Omit<
  React.HTMLAttributes<HTMLHeadingElement>,
  "children" | "className"
>;
type ParagraphHTMLAttributes = Omit<
  React.HTMLAttributes<HTMLParagraphElement>,
  "children" | "className"
>;
type SpanHTMLAttributes = Omit<React.HTMLAttributes<HTMLSpanElement>, "children" | "className">;

// Map variant to HTML attributes type
type HTMLAttributesForVariant<V extends TypographyVariant> = V extends
  | HeadingVariant
  | "subheading"
  | "page-heading"
  | "section-heading"
  ? HeadingHTMLAttributes
  : V extends "body"
    ? ParagraphHTMLAttributes
    : SpanHTMLAttributes;

// Base props shared by all variants
export interface BaseProps {
  children: React.ReactNode;
  className?: string;
}

// Main props type using conditional types for better autocomplete
type TypographyProps<V extends TypographyVariant = "body"> = BaseProps &
  HTMLAttributesForVariant<V> &
  (V extends SemanticVariant
    ? // Semantic variants have fixed styling - no weight or size props
      { variant: V }
    : // Other variants can have weight and optionally size
      {
        variant?: V;
        weight?: WeightForVariant<V>;
      } & (V extends HeadingVariant | "subheading" | "table-header"
        ? {}
        : V extends "button"
          ? { size?: ButtonSize }
          : V extends "placeholder"
            ? { size?: PlaceholderSize }
            : V extends "body" | "caption"
              ? { size?: BodySize }
              : {}));

const variantElementMap = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  subheading: "h5",
  body: "p",
  caption: "span",
  button: "span",
  "table-header": "span",
  placeholder: "span",
  "page-heading": "h2",
  "section-heading": "h3",
} as const;

export const Typography = <V extends TypographyVariant = "body">(props: TypographyProps<V>) => {
  const { variant = "body" as V, children, className, ...restProps } = props;

  // Set defaults based on variant
  let weight: "regular" | "medium" | "semibold";
  let size:
    | BodySize
    | "button-xs"
    | "button-sm"
    | "button-md"
    | "placeholder-regular"
    | "placeholder-large"
    | undefined;

  // Helper to safely access weight prop (not available on semantic variants)
  const getWeightProp = () => (props as { weight?: HeadingWeight | BodyWeight }).weight;

  // Extract typography-specific props that shouldn't be spread to the element
  const {
    weight: _weight,
    size: _size,
    ...htmlAttributes
  } = restProps as {
    weight?: HeadingWeight | BodyWeight;
    size?: BodySize | ButtonSize | PlaceholderSize;
    [key: string]: unknown;
  };

  if (variant === "page-heading") {
    // Semantic variant: fixed semibold weight, no size (not configurable)
    weight = "semibold";
    size = undefined;
  } else if (variant === "section-heading") {
    // Semantic variant: fixed medium weight, no size (not configurable)
    weight = "medium";
    size = undefined;
  } else if (
    variant === "h1" ||
    variant === "h2" ||
    variant === "h3" ||
    variant === "h4" ||
    variant === "subheading"
  ) {
    // Headings and subheading default to semibold, no size
    weight = (getWeightProp() as HeadingWeight | undefined) ?? "semibold";
    size = undefined;
  } else if (variant === "table-header") {
    // Table header: default medium weight, no size
    weight = (getWeightProp() as HeadingWeight | undefined) ?? "medium";
    size = undefined;
  } else if (variant === "body") {
    // Body defaults to regular weight and regular size
    const bodyProps = props as TypographyProps<"body">;
    weight = bodyProps.weight ?? "regular";
    size = bodyProps.size ?? "regular";
  } else if (variant === "caption") {
    // Caption: default to regular weight
    const captionProps = props as TypographyProps<"caption">;
    weight = captionProps.weight ?? "regular";
    size = captionProps.size;
  } else if (variant === "button") {
    // Button: fixed medium weight, default sm size
    const buttonProps = props as TypographyProps<"button">;
    weight = "medium";
    size = buttonProps.size ? `button-${buttonProps.size}` : "button-sm";
  } else {
    // Placeholder: fixed regular weight, default large size
    const placeholderProps = props as TypographyProps<"placeholder">;
    weight = "regular";
    size = placeholderProps.size ? `placeholder-${placeholderProps.size}` : "placeholder-large";
  }

  const Component = variantElementMap[variant] as keyof JSX.IntrinsicElements;

  return (
    <Component
      className={cn(typographyVariants({ variant, weight, size }), className)}
      {...htmlAttributes}
    >
      {children}
    </Component>
  );
};
